import { useCallback, useEffect, useSyncExternalStore } from "react";

const PERCENT_SCALE = 100;

/** Anzeigegrößen je Gerät und Konto (Faktor für CSS `zoom` am Dokument): 50–150 % in Zehnerschritten. */
export const DISPLAY_ZOOM_OPTIONS = [0.5, 0.6, 0.7, 0.8, 0.9, 1, 1.1, 1.2, 1.3, 1.4, 1.5] as const;
const DEFAULT_ZOOM = 1;
const listeners = new Set<() => void>();

function readZoom(storageKey: string): number {
  try {
    const stored = Number(window.localStorage.getItem(storageKey));
    return (DISPLAY_ZOOM_OPTIONS as readonly number[]).includes(stored) ? stored : DEFAULT_ZOOM;
  } catch {
    return DEFAULT_ZOOM;
  }
}

/**
 * Fensterbezogene Höhe, die trotz Anzeigegröße der echten Fensterhöhe entspricht, z. B.
 * `windowHeight("85vh")` oder `windowHeight("100dvh", "8px")` für „Fensterhöhe minus 8 px“.
 * CSS-Dateien schreiben dasselbe direkt: `calc(85vh / var(--mq-display-zoom, 1))`.
 */
export function windowHeight(viewportValue: string, minus?: string): string {
  const height = `${viewportValue} / var(--mq-display-zoom, 1)`;
  return minus ? `calc(${height} - ${minus})` : `calc(${height})`;
}

export function displayZoomLabel(value: number): string {
  return `${Math.round(value * PERCENT_SCALE)} %`;
}

/**
 * Anzeigegröße des angemeldeten Benutzers auf diesem Gerät (localStorage, nicht am Konto gespeichert):
 * Wert, Setzer und Anwendung als `zoom` am Dokument, solange die App-Oberfläche angezeigt wird.
 * Das Produkt wählt den Speicherschlüssel, z. B. je Konto.
 */
export function useDisplayZoom(storageKey: string): [number, (value: number) => void] {
  const subscribe = useCallback((listener: () => void) => {
    listeners.add(listener);
    return () => { listeners.delete(listener); };
  }, []);
  const value = useSyncExternalStore(subscribe, () => readZoom(storageKey), () => DEFAULT_ZOOM);
  const setValue = useCallback((next: number) => {
    try {
      window.localStorage.setItem(storageKey, String(next));
    } catch {
      // Ohne Speicher gilt die Größe nur bis zum Neuladen.
    }
    listeners.forEach((listener) => listener());
  }, [storageKey]);
  useEffect(() => {
    const root = document.documentElement;
    // `zoom` vergrößert auch Viewport-Einheiten: 100dvh wäre bei 50 % nur die halbe, bei 150 % die
    // anderthalbfache Fensterhöhe. Fensterbezogene Höhen teilen deshalb durch `--mq-display-zoom`.
    root.style.zoom = value === DEFAULT_ZOOM ? "" : String(value);
    if (value === DEFAULT_ZOOM) root.style.removeProperty("--mq-display-zoom");
    else root.style.setProperty("--mq-display-zoom", String(value));
    return () => {
      root.style.zoom = "";
      root.style.removeProperty("--mq-display-zoom");
    };
  }, [value]);
  return [value, setValue];
}
